import { refreshAuthSession } from "@/lib/auth/refresh-client";
import { uiLanguageOf, type UiLanguage } from "@/lib/site/site-routing";

type ApiEnvelope<T> = {
  ok: boolean;
  data?: T;
  meta?: Record<string, unknown>;
  error?: { message?: string; code?: string };
};

export class ApiError extends Error {
  status: number;
  code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export type ApiInit = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;

  signal?: AbortSignal;
};

export type ApiResult<T> = {
  data: T;

  meta: Record<string, unknown>;
};

export async function apiRequest<T>(
  url: string,
  init?: ApiInit,
): Promise<ApiResult<T>> {
  let { response, envelope } = await send<T>(url, init);

  if (response.status === 401 && envelope.error?.code === "UNAUTHORIZED") {
    const refreshed = await refreshAuthSession();

    if (refreshed.ok) {
      ({ response, envelope } = await send<T>(url, init));
    }
  }

  if (!response.ok || !envelope.ok) {
    throw new ApiError(
      envelope.error?.message ?? fallbackText().failed,
      response.status,
      envelope.error?.code ?? "ERROR",
    );
  }

  return { data: envelope.data as T, meta: envelope.meta ?? {} };
}

const FALLBACK_TEXT: Record<UiLanguage, { failed: string; network: string }> = {
  zh: { failed: "请求失败，请稍后重试", network: "网络异常，请稍后重试" },
  "zh-tw": { failed: "請求失敗，請稍後重試", network: "網路異常，請稍後重試" },
  en: {
    failed: "Request failed. Please try again later.",
    network: "Network error. Please try again later.",
  },
  ja: {
    failed: "リクエストに失敗しました。しばらくしてから再度お試しください",
    network: "ネットワークエラーです。しばらくしてから再度お試しください",
  },
  ko: {
    failed: "요청에 실패했습니다. 잠시 후 다시 시도해 주세요.",
    network: "네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.",
  },
  de: {
    failed: "Anfrage fehlgeschlagen. Bitte versuchen Sie es später erneut.",
    network: "Netzwerkfehler. Bitte versuchen Sie es später erneut.",
  },
  fr: {
    failed: "La requête a échoué. Veuillez réessayer plus tard.",
    network: "Erreur réseau. Veuillez réessayer plus tard.",
  },
  es: {
    failed: "La solicitud falló. Inténtelo de nuevo más tarde.",
    network: "Error de red. Inténtelo de nuevo más tarde.",
  },
  ru: {
    failed: "Не удалось выполнить запрос. Повторите попытку позже.",
    network: "Ошибка сети. Повторите попытку позже.",
  },
  pt: {
    failed: "A solicitação falhou. Tente novamente mais tarde.",
    network: "Erro de rede. Tente novamente mais tarde.",
  },
};

function fallbackText() {
  const lang = typeof document === "undefined" ? "" : document.documentElement?.lang;

  return FALLBACK_TEXT[lang ? uiLanguageOf(lang) : "zh"];
}

function siteHeaders(): Record<string, string> {
  if (typeof document === "undefined") {
    return {};
  }

  const code = document.documentElement.dataset.site;

  return code ? { "x-site": code } : {};
}

async function sendResponse(url: string, init?: ApiInit) {
  const hasBody = init?.body !== undefined;
  const headers = {
    ...(hasBody ? { "Content-Type": "application/json" } : {}),
    ...siteHeaders(),
    ...init?.headers,
  };
  return fetch(url, {
    method: init?.method ?? "GET",
    signal: init?.signal,
    ...(Object.keys(headers).length > 0 ? { headers } : {}),
    ...(hasBody ? { body: JSON.stringify(init.body) } : {}),
  });
}

export async function apiFetchStream(url: string, init?: ApiInit): Promise<Response> {
  let response = await sendResponse(url, init);
  if (response.status === 401) {
    const envelope = await response.clone().json().catch(() => null) as ApiEnvelope<unknown> | null;
    if (envelope?.error?.code === "UNAUTHORIZED" && (await refreshAuthSession()).ok) response = await sendResponse(url, init);
  }
  if (!response.ok) {
    const envelope = await response.json().catch(() => null) as ApiEnvelope<unknown> | null;
    throw new ApiError(envelope?.error?.message ?? fallbackText().network, response.status, envelope?.error?.code ?? "NETWORK");
  }
  return response;
}

async function send<T>(url: string, init?: ApiInit) {
  const response = await sendResponse(url, init);

  try {
    return { response, envelope: (await response.json()) as ApiEnvelope<T> };
  } catch {

    throw new ApiError(fallbackText().network, response.status, "NETWORK");
  }
}

export async function apiFetch<T>(url: string, init?: ApiInit): Promise<T> {
  return (await apiRequest<T>(url, init)).data;
}

export function metaTotal(meta: Record<string, unknown>): number {
  return typeof meta.total === "number" ? meta.total : 0;
}
